export default {
  namespace: 'domains',
  en: {
    title: 'Domain names',
    common: {
      domain: 'Domain name',
      status: 'Status',
      owner: 'Owner',
      admin: 'Admin',
      administrative: 'Administrative',
      registration: 'Registration date',
      expiresAt: 'Renewal date',
      active: 'Active',
      pending: 'Pending',
      failed: 'Failed',
      waiting: 'Waiting',
      inactive: 'Inactive',
      expired: 'Expired',
      suspend: 'Suspend',
      unknown: 'Unknown',
      verified: 'Verified',
      suspended: 'Suspended',
      quarantined: 'Quarantined',
      transferredOutbound: 'Transferred outbound',
      na: 'Not available',
      verificationStatus: 'Verification status',
      locked: 'Locked',
      updateInProgress: 'A {{type}} update process is already in progress or has been scheduled.',
      company: 'Company',
      person: 'Person',
      address: 'Address',
      phone: 'Phone',
      email: 'Email',
      name: 'Name',
      handle: 'Handle',
      transferToken: 'Transfer token',
      manage: 'Manage',
      actions: 'Actions',
      additionalData: 'Additional information',
      noHandlesFound: 'No handles found',
      searchingHandles: 'Searching handles...',
      handleIncomplete:
        'This handle cannot be selected due to missing or incorrect data. If needed, create a new handle with complete data.',
      product: 'Product',
      archived: 'Archived',
      dnssec: {
        show: 'Show DNSSEC status',
        tooltip: 'Look up the DNSSEC status of this domain name',
        enabled: 'Enabled',
        disabled: 'Disabled',
        unknown: 'Unknown',
        unknownTooltip: 'Please contact Exonet for more information',
      },
    },
    handles: {
      title: 'Handles',
      name: 'Name',
      domains: 'Number of domain names',
      createdDate: 'Created at',
      details: 'Details',
      update: {
        title: 'Domain name handles have changed',
        description:
          'The handles of domain names have recently been completely re-imported from the various registrars. Over time, small differences had arisen, which have now been corrected. As a result, it may happen that there are now more handles and that there are multiple handles that look alike. We will be merging and cleaning up this data soon, so you do not have to do it yourself.',
      },
    },
    modals: {
      manageHandle: {
        title: 'Manage Handle',
        sameAsOwner: 'Same as owner',
        createNewHandle: 'Create a new handle',
        adminWillBeSameAsOwner: 'The same as the owner of the domain name',
        chooseExistingHandle: 'Search and choose an existing handle',
        selectHandle: 'Select handle',
        searchHandle: 'Start typing to search handles...',
        initials: 'Initials',
        lastName: 'Last name',
        street: 'Street',
        number: 'Number',
        suffix: 'Suffix',
        zipCode: 'Zip code',
        city: 'City',
        stateProvince: 'State/Province',
        country: 'Country',
        existingHandle: 'Existing handle',
        personalInformation: 'Personal information',
        addressInformation: 'Address information',
        companyMax: 'Maximum of 255 characters allowed',
        initialsMax: 'Maximum of 255 characters allowed',
        initialsRequired: 'Please provide initials',
        lastNameMax: 'Maximum of 255 characters allowed',
        lastNameRequired: 'Please provide last name',
        emailValid: 'Please enter a valid email address',
        emailRequired: 'Please provide an email address',
        phoneFormat:
          'Please enter a phone number matching the E.164 format, for example 31612563478',
        streetMax: 'Maximum of 255 characters allowed',
        streetRequired: 'Please provide a street',
        numberTypeError: 'Please provide a number',
        numberRequired: 'Please provide a number',
        suffixMax: 'Maximum of 5 characters allowed',
        zipCodeRequired: 'A zipcode is required',
        cityMax: 'Maximum of 255 characters allowed',
        cityRequired: 'A city is required',
        provinceMax: 'Maximum of 255 characters allowed',
        provinceRequired: 'Please provide a state/province/county',
        countryRequired: 'Please provide a country',
        ownerRequired: 'Please select an owner',
        adminRequired: 'Please select an admin',
        submit: 'Save',
        noData: 'To be provided',
        transferTokenRequired: 'Please provide a transfer token',
        or: 'Or',
        useAsDefault: 'Use as default domain handle',
        cannotChooseCompanyAlert:
          'For this domain extension, a company cannot be chosen as administrative handle.',
        priceOverviewTitle: 'Costs',
        priceOverviewDescription: 'This change involves the following costs:',
      },
      manageNameservers: {
        title: 'Manage nameservers',
        add: 'Add nameserver',
        uniqueNameservers: 'Please enter unique nameservers',
        validDomain: 'Please enter a valid subdomain',
        atLeastTwo: 'Please enter at least 2 nameservers',
        required: 'Nameservers are required',
        submit: 'Save',
      },
      requestTransferToken: {
        title: 'Request transfer token',
        description:
          'This action will unlock the domain and send the transfer code to the customer’s default contact. If you prefer it to be sent to a different email address, please enter it below.',
        submit: 'Request',
        emailValid: 'Please enter a valid email address',
        dnssecStatusWarning:
          'We could not retrieve the DNSSEC status, so we are unsure whether it is enabled or disabled. DNSSEC being enabled during a transfer could cause problems.',
        dnssecWarning:
          'DNSSEC is enabled for this domain. We recommend disabling it and waiting 24 hours before starting the transfer, otherwise the domain might become unavailable.',
        removeDnssec: 'Deactivate DNSSEC',
      },
      retryRegistration: {
        title: 'Retry domain name registration',
        description:
          'The registration of this domain name failed. You can retry the registration, and update the nameservers or domain handles if needed.',
        submit: 'Retry registration',
        alert:
          'Registration of this domain name has failed. Please click the "Retry registration" button to attempt the registration again.',
      },
      editHandle: {
        title: 'Edit Handle',
        validation: 'Alias must be exactly 7 alphanumeric (A-Z, 0-9) characters',
      },
      readOnly: {
        alert:
          'Changes to this domain name are not supported. Please contact Exonet for assistance.',
        submit: 'Create ticket',
      },
    },
  },
  nl: {
    title: 'Domeinnamen',
    common: {
      domain: 'Domeinnaam',
      status: 'Status',
      owner: 'Eigenaar',
      admin: 'Admin',
      administrative: 'Administratief',
      registration: 'Registratiedatum',
      expiresAt: 'Verlengingsdatum',
      active: 'Actief',
      pending: 'In afwachting',
      failed: 'Mislukt',
      waiting: 'Wachtend',
      inactive: 'Inactief',
      expired: 'Verlopen',
      suspend: 'Geschorst',
      unknown: 'Onbekend',
      verified: 'Geverifieerd',
      suspended: 'Geschorst',
      quarantined: 'In quarantaine',
      transferredOutbound: 'Wegverhuisd',
      na: 'Niet beschikbaar',
      verificationStatus: 'Verificatiestatus',
      locked: 'Vergrendeld',
      updateInProgress: 'Een {{type}} update proces is al bezig of is al gepland.',
      company: 'Bedrijf',
      person: 'Persoon',
      address: 'Adres',
      phone: 'Telefoon',
      email: 'E-mailadres',
      name: 'Naam',
      handle: 'Handle',
      transferToken: 'Verhuiscode',
      manage: 'Beheer',
      actions: 'Acties',
      additionalData: 'Aanvullende informatie',
      noHandlesFound: 'Geen handles gevonden',
      searchingHandles: 'Zoeken naar handles...',
      handleIncomplete:
        'Deze handle kan niet worden geselecteerd vanwege ontbrekende of onjuiste gegevens. Maak indien nodig een nieuwe handle aan met volledige gegevens.',
      product: 'Product',
      archived: 'Gearchiveerd',
      dnssec: {
        show: 'Toon DNSSEC-status',
        tooltip: 'Bekijk de DNSSEC-status van deze domeinnaam',
        enabled: 'Ingeschakeld',
        disabled: 'Uitgeschakeld',
        unknown: 'Onbekend',
        unknownTooltip: 'Neem contact op met Exonet voor meer informatie',
      },
    },
    handles: {
      title: 'Handles',
      name: 'Naam',
      domains: 'Aantal domeinnamen',
      createdDate: 'Aangemaakt op',
      details: 'Details',
      update: {
        title: 'Domeinnaam-handles zijn veranderd',
        description:
          'De handles van domeinnamen zijn onlangs volledig opnieuw geïmporteerd vanuit de verschillende registrars. In de loop van de tijd waren er kleine verschillen ontstaan, die nu zijn gecorrigeerd. Hierdoor kan het voorkomen dat er nu meer handles zijn en er meerdere handles zijn die op elkaar lijken. Deze data gaan we binnenkort samenvoegen en opruimen, dit hoef je dus niet zelf te doen.',
      },
    },
    modals: {
      manageHandle: {
        title: 'Beheer handle',
        sameAsOwner: 'Zelfde als eigenaar',
        createNewHandle: 'Maak een nieuwe handle',
        adminWillBeSameAsOwner: 'Gebruik dezelfde handle als voor de eigenaar van de domeinnaam',
        chooseExistingHandle: 'Zoek en kies een bestaande handle',
        selectHandle: 'Selecteer handle',
        searchHandle: 'Begin met typen om handles te zoeken...',
        initials: 'Initialen',
        lastName: 'Achternaam',
        street: 'Straat',
        number: 'Nummer',
        suffix: 'Toevoeging',
        zipCode: 'Postcode',
        city: 'Stad',
        stateProvince: 'Staat/Provincie',
        country: 'Land',
        existingHandle: 'Bestaande handle',
        personalInformation: 'Persoonlijke informatie',
        addressInformation: 'Adres informatie',
        companyMax: 'Maximaal 255 karakters toegestaan',
        initialsMax: 'Maximaal 255 karakters toegestaan',
        initialsRequired: 'Vul initialen in',
        lastNameMax: 'Maximaal 255 karakters toegestaan',
        lastNameRequired: 'Vul achternaam in',
        emailValid: 'Vul een geldig e-mailadres in',
        emailRequired: 'Vul een e-mailadres in',
        phoneFormat:
          'Vul een telefoonnummer in dat voldoet aan het E.164 formaat, bijvoorbeeld 31612563478',
        streetMax: 'Maximaal 255 karakters toegestaan',
        streetRequired: 'Vul een straatnaam in',
        numberTypeError: 'Vul een geldig nummer in',
        numberRequired: 'Vul een nummer in',
        suffixMax: 'Maximaal 5 karakters toegestaan',
        zipCodeRequired: 'Vul een postcode in',
        cityMax: 'Maximaal 255 karakters toegestaan',
        cityRequired: 'Vul een stad in',
        provinceMax: 'Maximaal 255 karakters toegestaan',
        provinceRequired: 'Vul een staat/provincie/regio in',
        countryRequired: 'Vul een land in',
        ownerRequired: 'Selecteer een eigenaar',
        adminRequired: 'Selecteer een admin',
        submit: 'Opslaan',
        noData: 'Nog in te vullen',
        transferTokenRequired: 'Vul een verhuiscode in',
        or: 'Of',
        useAsDefault: 'Gebruik als standaard domein handle',
        cannotChooseCompanyAlert:
          'Bij deze domeinextensie kan geen bedrijf als administratieve handle worden gekozen.',
        priceOverviewTitle: 'Kosten',
        priceOverviewDescription: 'Deze wijziging brengt de volgende kosten met zich mee:',
      },
      manageNameservers: {
        title: 'Beheer nameservers',
        add: 'Nameserver toevoegen',
        uniqueNameservers: 'Voer unieke nameservers in',
        validDomain: 'Voer een geldig subdomeinnaam in',
        atLeastTwo: 'Voer minimaal 2 nameservers in',
        required: 'Nameservers zijn verplicht',
        submit: 'Opslaan',
      },
      requestTransferToken: {
        title: 'Verhuiscode aanvragen',
        description:
          'Bij deze actie wordt de domeinnaam ontgrendeld en wordt de verhuiscode naar het standaardcontact van de klant gestuurd. Als je liever wilt dat dit naar een ander e-mailadres wordt gestuurd, vul dit dan hieronder in.',
        submit: 'Aanvragen',
        emailValid: 'Vul een geldig e-mailadres in',
        dnssecStatusWarning:
          'We konden de DNSSEC-status niet ophalen, dus we weten niet zeker of deze in- of uitgeschakeld is. Als DNSSEC ingeschakeld is tijdens een verhuizing kan dit problemen veroorzaken.',
        dnssecWarning:
          'DNSSEC is ingeschakeld voor deze domeinnaam. We raden aan dit uit te schakelen en 24 uur te wachten voordat je de verhuizing start, anders kan de domeinnaam onbereikbaar worden.',
        removeDnssec: 'DNSSEC deactiveren',
      },
      retryRegistration: {
        title: 'Domeinregistratie opnieuw proberen',
        description:
          'De registratie van de domeinnaam is mislukt. Je kunt de registratie opnieuw uitvoeren en de nameservers of domein handles bijwerken indien nodig.',
        submit: 'Registratie opnieuw proberen',
        alert:
          'Registratie van deze domeinnaam is mislukt. Klik op "registratie opnieuw proberen" om de registratie opnieuw uit te voeren.',
      },
      editHandle: {
        title: 'Handle wijzigen',
        validation: 'Alias moet precies 7 alfanumerieke (A-Z, 0-9) tekens bevatten',
      },
      readOnly: {
        alert:
          'Wijzigingen aan deze domeinnaam worden niet ondersteund. Neem contact op met Exonet voor ondersteuning.',
        submit: 'Ticket toevoegen',
      },
    },
  },
};
